import { Grid, Modal, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  Button,
  PageTitleAndSubTitle,
  TimeNavigatorChart,
  VerticalLabelValue,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import SvgClose from '@HomaHealth/ui-component-library/lib/_components/atoms/Icons/Close';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';
import DeepDiveConstants from '../../../core-utils/messages/DeepDiveConstants';
import { DeepDiveResultItem } from '../../../pages/DeepDivePage';
import { numberToCurrencyFormatter } from '../../../core-utils/helper-utils/helper';
import axios from 'axios';
import properties from '../../../core-utils/properties';
import {
  formatDeepDiveCostTrend,
  getDateInfo,
} from '../../../core-utils/helper-utils/deepDiveHelper';

export interface DeepDiveCostTrendModalProps {
  open: boolean;
  setOpen: any;
  handleOpenSnackBar?: any;
  selectedTrend?: DeepDiveResultItem | null;
  selectedTrendIdentifier?: string;
  payload: any;
  setAddTaskModalOpen?: any;
  isIncurredDate: boolean;
  isPaidDate: boolean;
}

const DeepDiveCostTrendModal: React.FC<DeepDiveCostTrendModalProps> = observer(
  ({
    open,
    setOpen,
    selectedTrend,
    selectedTrendIdentifier,
    payload,
    handleOpenSnackBar,
    setAddTaskModalOpen,
    isIncurredDate,
    isPaidDate,
  }) => {
    const useStyles = makeStyles()({
      modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      paper: {
        width: '1182px',
        height: '700px',
        padding: '24px 28px 40px 32px',
        backgroundColor: patientAccessTheme.palette.status.white,
        border: 'none !important',
        position: 'relative',
      },
      button: {
        padding: '0px',
      },
      scroll: scrollBarStyles,
      content: {
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'scroll',
        height: '588px',
      },
    });
    const { classes } = useStyles();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [chartData, setChartData] = useState<any>();
    const handleClose = () => {
      setOpen(false);
    };

    //Apis
    const getCostTrend = async () => {
      setIsLoading(true);
      await axios
        .post(
          properties.aiqBaseUrl +
            properties.deepDiveBaseUrl +
            properties.deepDiveCostTrend,
          {
            incurredStartDate: payload.minIncurred,
            incurredEndDate: payload.maxIncurred,
            paidStartDate: payload.minPaid,
            paidEndDate: payload.maxPaid,
            eg_nid: payload.eg_nid,
            aggregationParameter: payload.aggregationParameter,
            selectedCategory: {
              identifier: selectedTrendIdentifier,
              value: selectedTrend?.value,
            },
            claim_id_list: selectedTrend?.claimsList,
          },
        )
        .then(async (res) => {
          if (res?.status === 200) {
            const costTrendData = formatDeepDiveCostTrend(res?.data?.data);
            setChartData(costTrendData);
            setIsLoading(false);
          }
        })
        .catch((ex) => {
          handleOpenSnackBar &&
            handleOpenSnackBar('Something went wrong', 'error', 3000);
          setIsLoading(false);
          return ex;
        });
    };

    useEffect(() => {
      getCostTrend();
    }, []);

    return (
      <>
        <Modal open={open} onClose={handleClose} className={classes.modal}>
          <Paper className={classes.paper}>
            <Grid
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '30px',
              }}
            >
              <PageTitleAndSubTitle
                title={`${selectedTrend?.value} ${DeepDiveConstants.MODAL_TITLE}`}
                subTitle={''}
                subTitleStyles={{ width: '0px' }}
              />
              <SvgClose
                style={{ cursor: 'pointer' }}
                color={patientAccessTheme.palette.status.icon}
                onClick={handleClose}
              />
            </Grid>
            <Grid className={`${classes.scroll} ${classes.content}`}>
              <Grid
                style={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'space-between',
                }}
              >
                <VerticalLabelValue
                  title={DeepDiveConstants.CURRENT_VALUE}
                  value={
                    <Typography
                      fontSize="18px"
                      lineHeight="43px"
                      textColor={patientAccessTheme.palette.text.highemphasis}
                      fontType="medium"
                    >
                      {typeof selectedTrend?.total_paid_amount === 'string'
                        ? selectedTrend?.total_paid_amount
                        : numberToCurrencyFormatter(
                            selectedTrend?.total_paid_amount ?? 0,
                            true,
                            2,
                          )}
                    </Typography>
                  }
                  showInfo={true}
                  infoText={getDateInfo(payload, isIncurredDate, isPaidDate)}
                />
                {/* <VerticalLabelValue
                  title={DeepDiveConstants.CHANGE}
                  value={
                    <Typography
                      fontSize="18px"
                      lineHeight="43px"
                      textColor={'#D6545E'}
                      fontType="medium"
                    >
                      {selectedTrend?.percent?.toFixed(2) ?? '-'}
                    </Typography>
                  }
                /> */}
              </Grid>

              <TimeNavigatorChart
                chartData={chartData}
                dataSchema={[
                  {
                    name: 'Time',
                    type: 'date',
                    format: '%-m/%Y',
                  },
                  {
                    name: 'Value',
                    type: 'number',
                  },
                ]}
                yAxis={[
                  {
                    plot: {
                      value: 'Value',
                      type: 'line',
                      connectnulldata: true,
                    },
                    title: 'Cost($)',
                  },
                ]}
                paletteColors={[patientAccessTheme.palette.primary[400]]}
                showLegend={false}
                subTitle={''}
                isLoading={isLoading}
                customXAxisTitle={
                  <Grid
                    style={{
                      position: 'absolute',
                      bottom: '135px',
                      right: '23px',
                    }}
                  >
                    <Typography
                      fontSize="12px"
                      lineHeight="23px"
                      textColor={patientAccessTheme.palette.text.highemphasis}
                    >
                      Months
                    </Typography>
                  </Grid>
                }
                chartHeight="410"
                chartWidth="1080"
                hideTitleAndSubTitle={true}
                height="420px"
                width="1095px"
              />
            </Grid>

            <Grid
              style={{
                display: 'flex',
                justifyContent: 'right',
                margin: '0px 40px 40px 0px',
                position: 'absolute',
                right: '0px',
                bottom: '0px',
              }}
            >
              <Button
                label={
                  <Typography
                    textColor={patientAccessTheme.palette.primary.main}
                    fontSize="12px"
                    lineHeight="12px"
                    fontType={'medium'}
                  >
                    {DeepDiveConstants.CANCEL}
                  </Typography>
                }
                variant="outlined"
                height="36px"
                width="180px"
                styleProps={{ marginRight: '25px' }}
                borderRadius="4px"
                className={classes.button}
                onClick={() => {
                  handleClose();
                }}
              />
              {setAddTaskModalOpen && (
                <Button
                  label={
                    <Typography
                      textColor={patientAccessTheme.palette.status.white}
                      fontSize="12px"
                      lineHeight="12px"
                      fontType={'medium'}
                    >
                      {DeepDiveConstants.ADD_TO_TASK}
                    </Typography>
                  }
                  variant="contained"
                  color="primary"
                  height="36px"
                  width="180px"
                  borderRadius="4px"
                  className={classes.button}
                  onClick={() => {
                    setAddTaskModalOpen(true);
                  }}
                />
              )}
            </Grid>
          </Paper>
        </Modal>
      </>
    );
  },
);

export default DeepDiveCostTrendModal;
